
import { Vue, Component, Prop } from 'vue-property-decorator'
import TableSettings from '@/types/tableSettings'
import axios from 'axios'
import MediaItem from '@/types/mediaItem'
import { split } from 'lodash'
import Column from '@/types/column'
import SystemMessageDetailsModal from '@/views/communication/events/SystemMessageDetailsModal.vue'
import SystemMessageModel from '@/models/SystemMessageModel'
import ls from '@/ls'
import PageHeader from '@/components/base/PageHeader.vue'
import ColumnOptions from '@/components/listview/ColumnOptions.vue'
import BuildingBulkUpdater from '@/components/listview/BuildingBulkUpdater.vue'
import BuildingBulkCreate from '@/components/building/BuildingBulkCreate.vue'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import { SystemMessageData } from '@/types/systemMessage'
import { can } from '@/globalFunctions'

@Component({
  methods: { split, can },
  components: { SystemMessageDetailsModal, ActiveFilters, BuildingBulkCreate, BuildingBulkUpdater, ColumnOptions, PageHeader }
})

export default class SystemMessageOverview extends Vue {
  @Prop({ type: Boolean }) editable!: boolean
  @Prop({ type: Array }) types!: string[]
  @Prop({ type: Array }) targets!: string[]
  @Prop({ type: Number }) triggerId!: number
  @Prop({ type: Boolean, default: false }) showNewButton!: boolean

  loading = false
  loadingPreview = false
  messageModalVisible = false
  previewModalVisible = false
  preview: MediaItem = new MediaItem()
  previewMode: 'media'|'sample' = 'media'
  samplePreview: SystemMessageData = new SystemMessageData()

  localStorageName = 'eventMessagesTableSettings'
  permissions = { show: 'system_messages.view', edit: 'system_messages.edit', delete: 'system_messages.delete' }
  sortOrder = { column: 'created_at', order: 'descend' }
  conditions = {}
  localSettings = ls.get(this.localStorageName)
  tableSettings:TableSettings = TableSettings.from(this.localSettings, this.conditions, this.sortOrder)

  filters: {
    categories: string[],
    types: string[],
    targets: string[]
  } = {
    categories: [],
    types: [],
    targets: []
  }

  messageId: number|null = null
  messageModel = new SystemMessageModel()
  systemMessages: SystemMessageData[] = []
  systemMessage: SystemMessageData|null = null
  relatedFiles: SystemMessageData[] = []

  get columns (): Column[] {
    return [
      {
        title: 'Bericht',
        dataIndex: 'name',
        key: 'name',
        scopedSlots: { customRender: 'name' },
        sorter: (a: SystemMessageData, b: SystemMessageData) => a.name.localeCompare(b.name)
      },
      {
        title: 'Doelgroepen',
        dataIndex: 'targets',
        key: 'targets',
        scopedSlots: { customRender: 'targets' },
        sorter: (a: SystemMessageData, b: SystemMessageData) => a.targets.length - b.targets.length
      },
      {
        title: 'Soort',
        dataIndex: 'type',
        key: 'type',
        width: 100,
        scopedSlots: { customRender: 'type' },
        sorter: (a: SystemMessageData, b: SystemMessageData) => a.type - b.type
      },
      { title: 'Acties', dataIndex: 'id', key: 'action', width: 100, scopedSlots: { customRender: 'action' }, align: 'right' }
    ]
  }

  mounted () : void {
    this.getSystemMessages()
  }

  public getSystemMessages (): void {
    if (this.triggerId === undefined || this.loading) return
    this.loading = true
    this.messageModel.where('trigger_id', this.triggerId).get().then((response: any) => {
      this.systemMessages = response || []
      this.loading = false
    }).catch((error) => {
      console.log(error)
      this.loading = false
    }).then(() => {
      this.relatedFiles = this.systemMessages.filter((msg) => msg.type === 0)
    })
  }

  get previewTitle (): string {
    return this.previewMode === 'sample' ? 'Voorbeeld berichtinhoud' : 'Voorbeeld'
  }

  getPreviewHtml (message: SystemMessageData) : void {
    this.loading = true
    if (message === undefined || this.loadingPreview) {
      this.loading = false
      return
    }

    if (message.auto_managed) {
      this.getSamplePreview(message.id)
      return
    }

    this.loadingPreview = true
    const payload = {
      id: message.id
    }
    axios.post(process.env.VUE_APP_API_URL + '/system-messages/preview', payload)
      .then((response) => {
        this.preview = response.data
        this.previewMode = 'media'

        if (this.preview.type === 'application/pdf') {
          window.open(this.preview.url, '_blank')
          this.previewModalVisible = false
          return
        }
        this.previewModalVisible = true
      }).catch((error) => {
        console.log(error)
        return ''
      })
      .finally(() => {
        this.loadingPreview = false
        this.loading = false
      })
  }

  getSamplePreview (messageId: number) : void {
    this.loadingPreview = true
    axios.get(process.env.VUE_APP_API_URL + '/system-messages/' + messageId)
      .then((response) => {
        this.samplePreview = response.data
        this.previewMode = 'sample'
        this.previewModalVisible = true
      }).catch((error) => {
        console.log(error)
      }).finally(() => {
        this.loadingPreview = false
        this.loading = false
      })
  }

  capitalize (s: string|undefined) : string {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
  }

  targetLabels (targetList: string, message: SystemMessageData): string[] {
    if (message.target_labels.length) {
      return message.target_labels
    }

    return targetList.split(',').map((target) => this.targets[Number(target)])
  }

  get activeFilters () : Record<string, (string|number|boolean)[]> {
    return this.tableSettings.activeFilters
  }

  onTableChanged (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    this.tableSettings.activeFilters = filters
    this.tableSettings.pagination = pagination
    this.tableSettings.sort = sort
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          if (can(this.permissions.edit)) {
            this.systemMessage = this.systemMessages.find((msg) => msg.id === rowData.id) as SystemMessageData
            this.messageId = rowData.id
            this.messageModalVisible = true
            this.loading = true
          } else {
            const message = this.systemMessages.find((msg) => msg.id === rowData.id) as SystemMessageData
            this.getPreviewHtml(message)
          }
        }
      }
    }
  }

  deleteMessage (id: number) : void {
    this.loading = true
    axios.delete(process.env.VUE_APP_API_URL + '/system-messages/' + id)
      .finally(() => {
        this.loading = false
        this.getSystemMessages()
      })
  }

  get canEdit () : boolean {
    return can(this.permissions.edit)
  }

  onCreateNewMessageClicked () : void {
    this.messageId = null
    this.messageModalVisible = true
    this.loading = true
  }

  onMessageModalClosed (updated: boolean) : void {
    this.messageModalVisible = false
    this.messageId = null
    this.loading = false

    if (updated) {
      this.getSystemMessages()
    }
  }
}
